import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { useCircularExpansionData } from './hooks';
import RunningLine from './RunningLine';

import { ANIMATION_COMPLETE_CLASS } from 'utils/animations/useBounceTextEnterAnimation';
import { MASK_VARIABLES } from 'utils/animations/useMaskCircle';
import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';
import { filterProps } from 'utils/helpers';

export type TCircularExpansionSection = Normalize<Queries.HomeContentCircularExpansionDataFragment>;

const circleYCoordinate = 600;
const CIRCLE_RADIUS = 350;

const runningLineYCoordinate = 1230;

enum CIRCULAR_EXPANSION_VARIABLES {
  circleRadius = '--circle-radius',
  circleYCoordinate = '--circle-y-coordinate',
  runningLineYCoordinate = '--running-line-y-coordinate',
  maskColor = '--mask-color',
}

enum TEMPLATE_ROW_LABELS {
  BACKGROUND_START = 'background-start',
  CIRCLE_CONTENT_START = 'circle-content-start',
  CIRCLE_CONTENT_END = 'circle-content-end',
  RUNNING_LINE = 'running-line',
  RUNNING_LINE_END = 'running-line-end',
  BACKGROUND_END = 'background-end',
}

const Container = styled.div`
  ${({ theme: { mq, utils } }) => css`
    ${CIRCULAR_EXPANSION_VARIABLES.circleRadius}: ${utils.rem(CIRCLE_RADIUS)};
    ${CIRCULAR_EXPANSION_VARIABLES.circleYCoordinate}: ${utils.rem(circleYCoordinate)};
    ${CIRCULAR_EXPANSION_VARIABLES.runningLineYCoordinate}: ${utils.rem(runningLineYCoordinate)};

    display: grid;
    grid-template-rows:
      [ ${TEMPLATE_ROW_LABELS.BACKGROUND_START}]
      calc(
        var(${CIRCULAR_EXPANSION_VARIABLES.circleYCoordinate}) -
          var(${CIRCULAR_EXPANSION_VARIABLES.circleRadius})
      )
      [ ${TEMPLATE_ROW_LABELS.CIRCLE_CONTENT_START}]
      calc(var(${CIRCULAR_EXPANSION_VARIABLES.circleRadius}) * 2)
      [ ${TEMPLATE_ROW_LABELS.CIRCLE_CONTENT_END}]
      calc(
        var(${CIRCULAR_EXPANSION_VARIABLES.runningLineYCoordinate}) -
          var(${CIRCULAR_EXPANSION_VARIABLES.circleRadius}) -
          var(${CIRCULAR_EXPANSION_VARIABLES.circleYCoordinate})
      )
      [ ${TEMPLATE_ROW_LABELS.RUNNING_LINE}]
      1fr
      [ ${TEMPLATE_ROW_LABELS.BACKGROUND_END}];
    grid-template-columns: 1fr;
    width: 100%;
    overflow: hidden;

    ${mq.md} {
      ${CIRCULAR_EXPANSION_VARIABLES.circleRadius}: ${utils.rem(60)};
      ${CIRCULAR_EXPANSION_VARIABLES.circleYCoordinate}: ${utils.rem(146)};
      ${CIRCULAR_EXPANSION_VARIABLES.runningLineYCoordinate}: ${utils.rem(384)};
    }
  `}
`;

const BackgroundImage = styled(GatsbyImage)`
  grid-row: ${TEMPLATE_ROW_LABELS.BACKGROUND_START} / ${TEMPLATE_ROW_LABELS.BACKGROUND_END};
  grid-column: 1 / -1;
`;

const RunningLineContainer = styled.div`
  display: grid;
  grid-row: ${TEMPLATE_ROW_LABELS.RUNNING_LINE} / ${TEMPLATE_ROW_LABELS.BACKGROUND_END};
  grid-column: 1 / -1;
  align-content: flex-start;
  transform: translateY(-50%);
  z-index: 1;
`;

const ContentContainer = styled.div`
  ${({ theme: { colors, mq, spacings } }) => css`
    display: flex;
    position: relative;
    grid-row: ${TEMPLATE_ROW_LABELS.CIRCLE_CONTENT_START} /
      ${TEMPLATE_ROW_LABELS.CIRCLE_CONTENT_END};
    grid-column: 1 / -1;
    column-gap: 50rem;
    align-items: center;
    justify-content: center;
    z-index: 1;

    &.${ANIMATION_COMPLETE_CLASS} {
      ${CIRCULAR_EXPANSION_VARIABLES.maskColor}: ${colors.primary};
    }

    ${mq.md} {
      column-gap: 0;
      justify-content: space-between;
      padding: 0 ${spacings.padding};
    }
  `}
`;

const Circle = styled.div`
  ${({ theme: { colors } }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(var(${MASK_VARIABLES.CIRCLE_SCALE}));
    border-radius: 50%;
    background-color: ${colors.white}1A;
    height: 100%;
    aspect-ratio: 1 / 1;
  `}
`;

const TitleContainer = styled('div', filterProps('alignRight'))<{
  alignRight?: boolean;
}>`
  ${({ alignRight = false }) => css`
    display: flex;
    flex-direction: column;
    align-items: ${alignRight ? 'flex-end' : 'flex-start'};
    z-index: 1;
  `}
`;

const TitleLineWrapper = styled.div`
  display: grid;
`;

const Title = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    grid-row: 1 / span 1;
    grid-column: 1 / span 1;

    & > h2 {
      margin: 0 -0.75rem -0.5rem 0;
      padding: 0 0.75rem 0.5rem 0;
      line-height: 0.76;
      letter-spacing: -0.065em;
      color: #deb03a;
      font-size: 23.5rem;
      -webkit-text-stroke: 1px #deb03a;

      ${tp.useFont(fonts => fonts.Switzer.regular)};
    }

    ${mq.md} {
      & > h2 {
        letter-spacing: -0.11em;
        font-size: 5.5rem;
      }
    }
  `}
`;

const TitleMask = styled(Title)`
  ${({ theme: { durations } }) => css`
    & > h2 {
      transition: background-color ${durations.long};
      background-clip: text;
      background-color: var(${CIRCULAR_EXPANSION_VARIABLES.maskColor});
      clip-path: circle(
        calc(
            var(${CIRCULAR_EXPANSION_VARIABLES.circleRadius}) * var(${MASK_VARIABLES.CIRCLE_SCALE})
          )
          at var(${MASK_VARIABLES.TEXT_MASK_X}) var(${MASK_VARIABLES.TEXT_MASK_Y})
      );
      -webkit-text-stroke: 1px transparent;
      -webkit-text-fill-color: transparent;
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, mq, tp } }) => css`
    position: absolute;
    bottom: 10rem;
    left: 50%;
    transform: translateX(-50%);

    p,
    strong {
      text-align: center;
      line-height: 1.19;
      color: #f4f4f4e6;
      font-size: 3.25rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }

    strong {
      color: ${colors.primary};
    }

    ${mq.md} {
      bottom: calc(-50% - 2.5rem);
      padding: 0 6rem;
      width: 100%;

      p,
      strong {
        line-height: 0.875;
        letter-spacing: -0.07em;
        font-size: 2.1875rem;

        ${tp.useFont(fonts => fonts.Switzer.lightest)};
      }

      br {
        display: none;
      }
    }
  `}
`;

const CircularExpansionSection: React.FC<TCircularExpansionSection> = sliceData => {
  const {
    containerRef,
    contentRef,
    getLeftTextRef,
    getRightTextRef,
    backgroundImage,
    description,
    leftText,
    rightText,
    primaryLineTexts,
    secondaryLineTexts,
  } = useCircularExpansionData(sliceData);

  if (!backgroundImage) return null;

  return (
    <Container ref={containerRef}>
      <BackgroundImage {...backgroundImage} />
      <ContentContainer ref={contentRef}>
        <TitleContainer alignRight>
          {leftText.map(leftTextLine => {
            const key = JSON.stringify(leftTextLine);
            return (
              <TitleLineWrapper key={key} ref={getLeftTextRef(key)}>
                <Title render={leftTextLine} />
                <TitleMask render={leftTextLine} />
              </TitleLineWrapper>
            );
          })}
        </TitleContainer>
        <Circle />
        <Description render={description} />
        <TitleContainer>
          {rightText.map(rightTextLine => {
            const key = JSON.stringify(rightTextLine);
            return (
              <TitleLineWrapper key={key} ref={getRightTextRef(key)}>
                <Title render={rightTextLine} />
                <TitleMask render={rightTextLine} />
              </TitleLineWrapper>
            );
          })}
        </TitleContainer>
      </ContentContainer>
      <RunningLineContainer>
        <RunningLine texts={primaryLineTexts} />
        <RunningLine texts={secondaryLineTexts} reverse />
      </RunningLineContainer>
    </Container>
  );
};

export const contentCircularExpansionFragment = graphql`
  fragment HomeContentCircularExpansionData on PrismicHomeDataContentHomecircularexpansion {
    slice_type
    primary {
      home_circular_expansion_background_image {
        gatsbyImageData
        alt
      }
      home_circular_expansion_description {
        richText
      }
      home_circular_expansion_left_text {
        richText
      }
      home_circular_expansion_right_text {
        richText
      }
    }
    items {
      ...HomeContentCircularExpansionRunningTextData
    }
  }

  fragment HomeContentCircularExpansionRunningTextData on PrismicHomeDataContentHomecircularexpansionItem {
    home_circular_expansion_running_text
  }
`;

export default CircularExpansionSection;
